import { VFC } from 'react';
import { Modal, Progress, Row, Space } from 'antd';
import { Button } from 'components/Button/Button';
import { SynchronizeStatus, SynchronizeStep } from 'consts/sync';
import { useStore as useInventorizeStore } from 'routes/inventorize_meter/useStore';
import { useStore as useInstallStore } from 'routes/installation/useStore';
import { useSynchronizeInstallationMeters } from 'hooks/useSynchronizeInstallMeters';
import { useSynchronizeMeters } from 'hooks/useSynchronizeMeters';
import { useSyncModalStore } from 'components/Modal/store';
import { useReplaceMeterStore } from 'routes/meter_replacement/useStore';
import { useSynchronizeReplaceMeters } from 'hooks/useSynchronizeReplaceMeters';
import { FormattedMessage } from 'react-intl';
import { Modal as messages } from './messages';

const getModalContent = ({ queuedCount, syncedCount, rejectedCount, syncStep }: SynchronizeStatus) => {
  switch (syncStep) {
    case SynchronizeStep.Confirm:
      return (
        <>
          <span>
            <FormattedMessage
              {...messages.confirm}
              values={{
                queuedCount: <strong>{queuedCount}</strong>,
              }}
            />
          </span>
          <Progress format={() => '0 %'} percent={0} type="circle" />
        </>
      );
    case SynchronizeStep.GetReady:
      return (
        <>
          <span>
            <FormattedMessage {...messages.getReady} />
          </span>
          <Progress format={() => '1 %'} percent={1} type="circle" />
        </>
      );
    case SynchronizeStep.InProgress: {
      const percentage = Math.round(((syncedCount + rejectedCount) * 100) / queuedCount);
      return (
        <>
          <span>
            <FormattedMessage {...messages.inProgress} />
          </span>
          <Progress format={() => `${percentage} %`} percent={percentage} type="circle" />
          <span>
            OK: {syncedCount} | NOK: {rejectedCount}
          </span>
        </>
      );
    }
    case SynchronizeStep.Finished:
      return (
        <>
          <span>
            <FormattedMessage {...messages.finished} />
          </span>
          <Progress format={() => '100 %'} percent={100} type="circle" />
          <span>
            OK: {syncedCount} | NOK: {rejectedCount}
          </span>
        </>
      );
    default:
      /* eslint-disable-next-line */
      console.error(`Unsupported synchronize step: ${syncStep}`);
      return null;
  }
};

export const SyncModal: VFC = () => {
  const { setState: setSyncModalState, syncModalType } = useSyncModalStore();

  const { syncOfflineRecords: syncInstalledMeters, syncStatus: syncInstalledStatus } =
    useSynchronizeInstallationMeters();
  const { syncOfflineRecords: syncInventoryMeters, syncStatus: syncInventoryStatus } = useSynchronizeMeters();
  const { syncOfflineRecords: syncReplaceMeters, syncStatus: syncReplaceMetersStatus } = useSynchronizeReplaceMeters();

  const getSyncRecords = () => {
    switch (syncModalType) {
      case 'install':
        return syncInstalledMeters;
      case 'pasport':
        return syncInventoryMeters;
      case 'replace':
        return syncReplaceMeters;
      default:
        console.error(`Unsupported sync modal type: ${syncModalType}`);
        return () => {};
    }
  };

  const getSyncStatus = () => {
    switch (syncModalType) {
      case 'install':
        return syncInstalledStatus;
      case 'pasport':
        return syncInventoryStatus;
      case 'replace':
        return syncReplaceMetersStatus;
      default:
        console.error(`Unsupported sync modal type: ${syncModalType}`);
        return {} as SynchronizeStatus;
    }
  };

  const syncRecords = getSyncRecords();
  const syncStatus = getSyncStatus();

  const { isSendingData: isSendingInstallData } = useInstallStore();
  const { isSendingData: isSendingInventorizedData } = useInventorizeStore();
  const { isSendingData: isSendingReplaceData } = useReplaceMeterStore();
  const isLoading = isSendingInstallData || isSendingInventorizedData || isSendingReplaceData;

  return (
    <Modal
      open
      confirmLoading={isLoading}
      footer={
        syncStatus.syncStep === SynchronizeStep.Finished ? (
          <Button block onClick={() => setSyncModalState(false)}>
            <FormattedMessage {...messages.close} />
          </Button>
        ) : (
          <Row wrap={false}>
            <Button block loading={isLoading} onClick={() => setSyncModalState(false)}>
              <FormattedMessage {...messages.cancel} />
            </Button>
            <Button key="submit" block loading={isLoading} type="primary" onClick={() => syncRecords()}>
              {isLoading ? <FormattedMessage {...messages.sending} /> : <FormattedMessage {...messages.launch} />}
            </Button>
          </Row>
        )
      }
      title={
        <FormattedMessage
          {...messages.recordSynchronization}
          values={{
            type:
              syncModalType === 'install' ? (
                <FormattedMessage {...messages.installationMeters} />
              ) : (
                <FormattedMessage {...messages.passportedMeters} />
              ),
          }}
        />
      }
      onCancel={() => setSyncModalState(false)}
    >
      <Space align="center" className="w-100" direction="vertical">
        {getModalContent(syncStatus)}
      </Space>
    </Modal>
  );
};
